import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface StackedBarItem {
  label: string;
  [key: string]: any;
}

export interface StackedBarLegend {
  key: string;
  label: string;
  color: string;
}

@Component({
  selector: 'app-multi-stacked-bar-chart',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="w-full h-full relative flex flex-col">
      <div class="flex-grow w-full relative" [class.overflow-x-auto]="scrollable" [class.overflow-y-hidden]="scrollable">
        <div #chartContainer class="absolute top-0 left-0 h-full w-full" 
             [style.min-width]="scrollable && data.length > 5 ? (data.length * 160) + 'px' : '100%'"></div>
      </div>
      <div *ngIf="showLegend && legendData.length" class="flex flex-wrap justify-center gap-4 mt-4 mb-2">
        <div *ngFor="let item of legendData" 
             class="flex items-center gap-2 cursor-pointer transition-opacity duration-200" 
             [class.opacity-40]="hiddenKeys.has(item.key)" 
             (click)="toggleLegend(item.key)">
          <div class="w-3 h-3 rounded-sm" [style.background-color]="item.color"></div>
          <span class="text-xs text-slate-600 dark:text-slate-400 select-none">{{ item.label }}</span>
        </div>
      </div>
      <!-- Tooltip -->
      <div #tooltip class="absolute hidden bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-lg rounded-lg p-3 pointer-events-none z-50 text-sm transition-opacity duration-200">
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
  `]
})
export class MultiStackedBarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  @ViewChild('tooltip') tooltipEl!: ElementRef;

  @Input() data: StackedBarItem[] = [];
  @Input() legendData: StackedBarLegend[] = [];
  @Input() showLegend: boolean = true;
  @Input() showYAxis: boolean = true;
  @Input() showXAxis: boolean = true;
  @Input() barPadding: number = 0.3;
  @Input() maxBarWidth: number = 40;
  @Input() yAxisTicks: number = 5;
  @Input() showOnlyFirstLastXAxis: boolean = true;
  @Input() scrollable: boolean = false;
  @Input() formatKM: boolean = true;
  // When true, hovering a bar shows every non-zero series (colored dot + count) for that
  // bucket plus a Total row, instead of only the hovered segment's value. Opt-in so existing
  // consumers of this shared chart keep their current single-series tooltip.
  @Input() tooltipShowAllSeries: boolean = false;

  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;
  private themeService = inject(ThemeService);

  ngAfterViewInit() {
    this.setupResizeObserver();
    this.themeSubscription = this.themeService.theme$.subscribe(() => {
      this.renderChart();
    });
    setTimeout(() => this.renderChart(), 0);
  }

  hiddenKeys = new Set<string>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] || changes['legendData']) {
      // Don't reset hiddenKeys on data update to keep user preference unless needed
      this.renderChart();
    }
  }

  toggleLegend(key: string) {
    if (this.hiddenKeys.has(key)) {
      this.hiddenKeys.delete(key);
    } else {
      this.hiddenKeys.add(key);
    }
    this.renderChart();
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.themeSubscription?.unsubscribe();
  }

  private setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.renderChart();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  private renderChart() {
    if (!this.data || !this.data.length || !this.legendData || !this.legendData.length || !this.chartContainer) return;

    const container = d3.select(this.chartContainer.nativeElement);

    const rect = this.chartContainer.nativeElement.getBoundingClientRect();
    const width = rect.width || 600;
    const height = rect.height || 300;

    if (width === 0 || height === 0) return;

    const margin = { top: 20, right: 20, bottom: this.showXAxis ? 30 : 10, left: this.showYAxis ? 65 : 10 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const isDark = this.themeService.getTheme() === 'dark';
    const textColor = isDark ? '#a1a1aa' : '#64748b'; // slate-400 / slate-500
    const gridColor = isDark ? '#2e2e33' : '#f1f5f9'; // slate-700 / slate-100
    const axisColor = isDark ? '#2e2e33' : '#e2e8f0';

    let svg = container.select<SVGSVGElement>('svg');
    let g: d3.Selection<SVGGElement, unknown, null, undefined>;

    if (svg.empty()) {
      svg = container.append('svg')
        .attr('width', '100%')
        .attr('height', '100%');
      g = svg.append('g').attr('class', 'main-group');
      g.append('g').attr('class', 'y-grid');
      g.append('g').attr('class', 'y-axis');
      g.append('g').attr('class', 'x-axis');
      g.append('g').attr('class', 'bars-group');
    } else {
      g = svg.select('.main-group') as any;
    }

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');
    g.attr('transform', `translate(${margin.left},${margin.top})`);

    const keys = this.legendData.map(l => l.key);

    // Stack the data, mapping hidden keys to 0
    const stack = d3.stack<StackedBarItem>()
      .keys(keys)
      .value((d, key) => this.hiddenKeys.has(key) ? 0 : (d[key] || 0));

    const stackedData = stack(this.data);

    // Scales
    const maxAllowedWidth = this.data.length * (this.maxBarWidth * 4); // max width per bar + gap
    const finalWidth = Math.min(innerWidth, Math.max(maxAllowedWidth, innerWidth * 0.5));
    const xOffset = (innerWidth - finalWidth) / 2;

    const x = d3.scaleBand()
      .domain(this.data.map(d => d.label))
      .range([xOffset, xOffset + finalWidth])
      .padding(this.barPadding);

    const maxVal = d3.max(stackedData[stackedData.length - 1], d => d[1]) || 0;

    const y = d3.scaleLinear()
      .domain([0, maxVal * 1.1]) // 10% padding top
      .range([innerHeight, 0])
      .nice(this.yAxisTicks);

    const transitionDuration = 500;

    // Grid lines
    if (this.showYAxis) {
      const yGrid = g.select('.y-grid') as any;
      yGrid.transition().duration(transitionDuration)
        .call(d3.axisLeft(y)
          .ticks(this.yAxisTicks)
          .tickSize(-innerWidth)
          .tickFormat(() => '')
        )
        .call((g: any) => g.select('.domain').remove())
        .call((g: any) => g.selectAll('.tick line')
          .attr('stroke', gridColor)
          .attr('stroke-width', 1)
        );

      // Y Axis labels
      const yAxis = g.select('.y-axis') as any;
      yAxis.transition().duration(transitionDuration)
        .call(d3.axisLeft(y).ticks(this.yAxisTicks).tickSize(0).tickFormat(d => {
          const num = Number(d);
          if (this.formatKM) {
            if (num >= 1000000) return `${(num / 1000000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
            if (num >= 1000) return `${(num / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`;
          }
          return num.toLocaleString('en-US', { maximumFractionDigits: 2 });
        }))
        .call((g: any) => g.select('.domain').remove())
        .call((g: any) => g.selectAll('.tick text')
          .attr('fill', textColor)
          .attr('font-size', '11px')
          .attr('dx', -5)
        );
    } else {
      g.select('.y-grid').selectAll('*').remove();
      g.select('.y-axis').selectAll('*').remove();
    }

    // X Axis
    if (this.showXAxis) {
      const tickValues = (this.showOnlyFirstLastXAxis && this.data.length > 1)
        ? [this.data[0].label, this.data[this.data.length - 1].label]
        : x.domain();

      const xAxis = g.select('.x-axis') as any;
      xAxis.attr('transform', `translate(0,${innerHeight})`)
        .transition().duration(transitionDuration)
        .call(d3.axisBottom(x).tickSize(0).tickValues(tickValues));

      xAxis.select('.domain').attr('stroke', axisColor);

      xAxis.selectAll('.tick text')
        .attr('fill', textColor)
        .attr('font-size', '11px')
        .attr('dy', 10)
        .attr('dx', 0)
        .attr('text-anchor', 'middle');
    } else {
      g.select('.x-axis').selectAll('*').remove();
    }

    // Color scale
    const colorScale = d3.scaleOrdinal<string>()
      .domain(keys)
      .range(this.legendData.map(l => l.color));

    const tooltip = d3.select(this.tooltipEl.nativeElement);

    const barsGroup = g.select('.bars-group');

    // Layers
    const layer = barsGroup.selectAll<SVGGElement, d3.Series<StackedBarItem, string>>('.layer')
      .data(stackedData, d => d.key);

    const layerEnter = layer.enter().append('g')
      .attr('class', 'layer')
      .attr('fill', d => colorScale(d.key));
    const layerUpdate = layerEnter.merge(layer);
    layer.exit().remove();

    // Bars
    const rects = layerUpdate.selectAll<SVGRectElement, d3.SeriesPoint<StackedBarItem>>('rect')
      .data(d => d.map(item => Object.assign(item, { key: d.key })), (d: any) => d.data.label);

    const rectsEnter = rects.enter().append('rect')
      .attr('x', (d: any) => {
        const bw = x.bandwidth();
        const baseOffset = x(d.data.label) || 0;
        return bw > this.maxBarWidth ? baseOffset + (bw - this.maxBarWidth) / 2 : baseOffset;
      })
      .attr('y', innerHeight)
      .attr('height', 0)
      .attr('width', () => {
        const bw = x.bandwidth();
        return bw > this.maxBarWidth ? this.maxBarWidth : bw;
      })
      .attr('class', 'cursor-pointer')
      .style('opacity', 1);

    const rectsUpdate = rectsEnter.merge(rects);
    rects.exit().remove();

    rectsUpdate.transition().duration(transitionDuration)
      .attr('x', (d: any) => {
        const bw = x.bandwidth();
        const baseOffset = x(d.data.label) || 0;
        return bw > this.maxBarWidth ? baseOffset + (bw - this.maxBarWidth) / 2 : baseOffset;
      })
      .attr('y', (d: any) => {
        const h = y(d[0]) - y(d[1]);
        const val = this.hiddenKeys.has(d.key) ? 0 : (d.data[d.key] || 0);
        if (h < 3 && val > 0) return y(d[1]) - (3 - h);
        return y(d[1]);
      })
      .attr('height', (d: any) => {
        const h = y(d[0]) - y(d[1]);
        const val = this.hiddenKeys.has(d.key) ? 0 : (d.data[d.key] || 0);
        if (val === 0) return 0;
        if (h < 3 && val > 0) return 3;
        return h;
      })
      .attr('width', () => {
        const bw = x.bandwidth();
        return bw > this.maxBarWidth ? this.maxBarWidth : bw;
      });

    // Hover events on the update selection
    rectsUpdate
      .on('mouseover', (event, d: any) => {
        if (this.hiddenKeys.has(d.key)) return;
        barsGroup.selectAll('rect').style('opacity', 0.5);
        d3.select(event.currentTarget).style('opacity', 1);

        const key = d.key;
        const legendItem = this.legendData.find(l => l.key === key);
        const val = d.data[key];
        const total = keys.reduce((sum, k) => sum + (this.hiddenKeys.has(k) ? 0 : (d.data[k] || 0)), 0);

        if (this.tooltipShowAllSeries) {
          const rowsHtml = keys
            .filter((k) => !this.hiddenKeys.has(k) && (d.data[k] || 0) > 0)
            .map((k) => {
              const item = this.legendData.find(l => l.key === k);
              return `
                <div class="flex items-center gap-2 mb-1">
                  <div class="w-2 h-2 rounded-sm" style="background-color: ${item?.color}"></div>
                  <span class="text-slate-600 dark:text-slate-300">${item?.label}: </span>
                  <span class="font-medium text-slate-900 dark:text-white">${Number(d.data[k]).toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
                </div>
              `;
            }).join('');

          tooltip.style('display', 'block')
            .html(`
              <div class="font-semibold text-slate-900 dark:text-white mb-1">${d.data.label}</div>
              ${rowsHtml}
              <div class="text-xs text-slate-500 mt-1 pt-1 border-t border-slate-100 dark:border-slate-700">
                Total: ${Number(total).toLocaleString('en-US', { maximumFractionDigits: 2 })}
              </div>
            `);
          return;
        }

        tooltip.style('display', 'block')
          .html(`
            <div class="font-semibold text-slate-900 dark:text-white mb-1">${d.data.label}</div>
            <div class="flex items-center gap-2 mb-1">
              <div class="w-2 h-2 rounded-sm" style="background-color: ${legendItem?.color}"></div>
              <span class="text-slate-600 dark:text-slate-300">${legendItem?.label}: </span>
              <span class="font-medium text-slate-900 dark:text-white">${Number(val).toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>
            </div>
            <div class="text-xs text-slate-500 mt-1 pt-1 border-t border-slate-100 dark:border-slate-700">
              Total: ${Number(total).toLocaleString('en-US', { maximumFractionDigits: 2 })}
            </div>
          `);
      })
      .on('mousemove', (event) => {
        const tooltipNode = this.tooltipEl.nativeElement;
        const containerRect = this.chartContainer.nativeElement.parentElement.getBoundingClientRect();

        let left = event.clientX - containerRect.left + 15;
        let top = event.clientY - containerRect.top - 15;

        if (left + tooltipNode.offsetWidth > containerRect.width) {
          left = event.clientX - containerRect.left - tooltipNode.offsetWidth - 15;
        }

        const overflowBottom = top + tooltipNode.offsetHeight > containerRect.height - 10 || event.clientY + tooltipNode.offsetHeight > window.innerHeight;
        if (overflowBottom || event.clientY - containerRect.top > containerRect.height / 2) {
          top = event.clientY - containerRect.top - tooltipNode.offsetHeight - 15;
        }
        if (top < 0) {
          top = 0;
        }

        tooltip.style('left', left + 'px')
          .style('top', top + 'px');
      })
      .on('mouseout', () => {
        barsGroup.selectAll('rect').style('opacity', 1);
        tooltip.style('display', 'none');
      });
  }
}
